'use client'

import Image from 'next/image'
import React from 'react'
import { motion } from 'framer-motion'
import { FaAward, FaGlobeEurope, FaUsers, FaArrowRight } from 'react-icons/fa'

const EASE: [number, number, number, number] = [0.2, 0.8, 0.2, 1]

const stats = [
    {
        icon: <FaAward aria-hidden='true' className='h-4 w-4' />,
        value: '20+ years',
        label: 'Executive coaching & training'
    },
    {
        icon: <FaUsers aria-hidden='true' className='h-4 w-4' />,
        value: 'Fortune 500',
        label: 'Leadership environments'
    },
    {
        icon: <FaGlobeEurope aria-hidden='true' className='h-4 w-4' />,
        value: 'Intercultural',
        label: 'Teams & complexity'
    }
]

const specialisation = [
    'Transformational leadership',
    'Intercultural team development',
    'Performance growth under pressure',
    'ADHD & EED coaching (cognitive diversity + emotional development)',
    'Conflict management'
]

export default function CoachSection() {
    return (
        <section id='coach' className='relative overflow-hidden bg-page text-text'>
            {/* Soft background */}
            <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                <div className='absolute -left-56 -top-56 h-190 w-190 rounded-full bg-[color-mix(in_oklab,var(--color-accent)_12%,transparent)] blur-3xl' />
                <div className='absolute -right-72 -bottom-72 h-230 w-230 rounded-full bg-[color-mix(in_oklab,var(--color-accent-2)_10%,transparent)] blur-3xl' />
                <div className='absolute inset-0 bg-[radial-gradient(1200px_700px_at_20%_0%,color-mix(in_oklab,var(--color-soft)_55%,transparent),transparent_60%)]' />
            </div>

            <div className='relative section-pad'>
                <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-12'>
                    <div className='grid gap-8 sm:gap-10 lg:grid-cols-12 lg:items-start'>
                        {/* ── IMAGE PANEL ── */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.15 }}
                            transition={{ duration: 0.75, ease: EASE }}
                            className='lg:col-span-7'>
                            <div className='relative overflow-hidden rounded-2xl shadow-[0_28px_80px_rgba(0,0,0,0.22)] sm:rounded-3xl'>
                                <Image
                                    src='/Nick-Parry.JPG'
                                    alt='Nick Parry — leadership trainer and executive coach'
                                    width={2000}
                                    height={2200}
                                    className='aspect-4/5 w-full object-cover md:aspect-3/4'
                                    priority={false}
                                />

                                {/* Gradient: subtle on mobile (just for name), full on md+ */}
                                <div
                                    aria-hidden='true'
                                    className='pointer-events-none absolute inset-0 bg-linear-to-t from-black/60 via-black/10 via-40% to-transparent md:hidden'
                                />
                                <div
                                    aria-hidden='true'
                                    className='pointer-events-none absolute inset-0 hidden md:block'
                                    style={{
                                        background:
                                            'radial-gradient(900px 620px at 22% 12%, rgba(79,140,255,0.18), transparent 55%), linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.50) 30%, rgba(0,0,0,0.10) 55%, rgba(0,0,0,0.0) 100%)'
                                    }}
                                />

                                {/* YOUR COACH pill */}
                                <div className='absolute left-4 top-4 sm:left-6 sm:top-6'>
                                    <div className='rounded-full bg-white/10 px-2.5 py-1.5 text-[10px] font-semibold tracking-[0.25em] text-white/90 backdrop-blur-sm sm:px-3 sm:py-2 sm:text-[11px] sm:tracking-[0.28em]'>
                                        YOUR COACH
                                    </div>
                                </div>

                                {/* Bottom overlay: name only on mobile, full content on md+ */}
                                <div className='absolute inset-x-0 bottom-0 p-4 pb-5 sm:p-6 sm:pb-7 md:p-8'>
                                    <p className='text-2xl font-semibold tracking-tight text-white sm:text-3xl md:text-4xl lg:text-5xl'>
                                        Nick Parry
                                    </p>

                                    {/* ── Rich overlay content: hidden on mobile, visible md+ ── */}
                                    <div className='hidden md:block'>
                                        <p className='mt-3 max-w-[52ch] text-sm leading-relaxed text-white/80 md:text-base'>
                                            Leadership trainer and executive coach with 20+ years supporting leaders in
                                            international and Fortune 500 environments. Clear structure, practical
                                            tools, measurable outcomes.
                                        </p>

                                        <div className='mt-6'>
                                            <p className='text-[11px] font-semibold tracking-[0.28em] text-white/60'>
                                                APPROACH
                                            </p>
                                            <p className='mt-2 text-sm font-semibold text-white'>
                                                Clear structure. Practical tools. Measurable outcomes — without noise.
                                            </p>
                                            <p className='mt-1.5 text-xs text-white/60'>
                                                Built for sustainable change in complex environments.
                                            </p>
                                        </div>

                                        <div className='mt-4 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-white/70'>
                                            <span className='inline-flex items-center gap-1.5'>
                                                <FaAward className='h-4 w-4 text-white/75' />
                                                <span className='font-semibold text-white/85'>20+ years</span>
                                            </span>
                                            <span className='inline-flex items-center gap-1.5'>
                                                <FaUsers className='h-4 w-4 text-white/75' />
                                                <span className='font-semibold text-white/85'>Fortune 500</span>
                                            </span>
                                            <span className='inline-flex items-center gap-1.5'>
                                                <FaGlobeEurope className='h-4 w-4 text-white/75' />
                                                <span className='font-semibold text-white/85'>Intercultural</span>
                                            </span>
                                        </div>

                                        <div className='mt-6 h-px w-full bg-[linear-gradient(to_right,transparent,rgba(255,255,255,0.25),transparent)]' />
                                    </div>
                                </div>
                            </div>

                            {/* ── Mobile content below image (visible only below md) ── */}
                            <div className='mt-5 space-y-4 px-1 md:hidden'>
                                <p className='text-sm leading-relaxed text-muted'>
                                    Leadership trainer and executive coach with 20+ years supporting leaders in
                                    international and Fortune 500 environments. Clear structure, practical tools,
                                    measurable outcomes.
                                </p>

                                <div>
                                    <p className='text-[10px] font-semibold tracking-[0.25em] text-muted/60'>
                                        APPROACH
                                    </p>
                                    <p className='mt-1.5 text-[13px] font-semibold'>
                                        Clear structure. Practical tools. Measurable outcomes — without noise.
                                    </p>
                                    <p className='mt-1 text-xs text-muted'>
                                        Built for sustainable change in complex environments.
                                    </p>
                                </div>

                                <div className='flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted'>
                                    <span className='inline-flex items-center gap-1.5'>
                                        <FaAward className='h-3.5 w-3.5 text-accent' />
                                        <span className='font-semibold text-text'>20+ years</span>
                                    </span>
                                    <span className='inline-flex items-center gap-1.5'>
                                        <FaUsers className='h-3.5 w-3.5 text-accent' />
                                        <span className='font-semibold text-text'>Fortune 500</span>
                                    </span>
                                    <span className='inline-flex items-center gap-1.5'>
                                        <FaGlobeEurope className='h-3.5 w-3.5 text-accent' />
                                        <span className='font-semibold text-text'>Intercultural</span>
                                    </span>
                                </div>

                                <div className='h-px w-full bg-[linear-gradient(to_right,transparent,color-mix(in_oklab,var(--color-border)_70%,transparent),transparent)]' />
                            </div>
                        </motion.div>

                        {/* ── RIGHT PANEL ── */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.15 }}
                            transition={{ duration: 0.7, ease: EASE, delay: 0.02 }}
                            className='lg:col-span-5 lg:pl-2'>
                            <p className='text-[11px] font-semibold tracking-[0.28em] text-muted'>
                                WHAT YOU CAN EXPECT
                            </p>

                            <h3 className='mt-3 text-balance text-2xl font-semibold tracking-tight sm:mt-4 sm:text-3xl lg:text-4xl'>
                                Structured, solution-oriented coaching that makes progress feel inevitable.
                            </h3>

                            <p className='mt-3 max-w-[62ch] text-pretty text-sm text-muted sm:mt-4 sm:text-base lg:text-lg'>
                                Designed for leaders operating under pressure: fewer loops, clearer decisions, better
                                delivery—without burning out your people.
                            </p>

                            {/* Stats */}
                            <div className='mt-6 sm:mt-8'>
                                <div className='grid gap-4 sm:gap-5'>
                                    {stats.map((s) => (
                                        <div key={s.value} className='flex items-start gap-3 sm:gap-4'>
                                            <span className='mt-0.5 grid h-9 w-9 flex-none place-items-center rounded-xl bg-[color-mix(in_oklab,var(--color-soft)_88%,transparent)] text-text sm:h-10 sm:w-10 sm:rounded-2xl'>
                                                {s.icon}
                                            </span>
                                            <div className='min-w-0'>
                                                <p className='text-sm font-semibold tracking-tight'>{s.value}</p>
                                                <p className='mt-0.5 text-xs text-muted sm:mt-1 sm:text-sm'>
                                                    {s.label}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className='mt-6 h-px w-full bg-[linear-gradient(to_right,transparent,color-mix(in_oklab,var(--color-border)_70%,transparent),transparent)] sm:mt-8' />
                            </div>

                            {/* Specialisation */}
                            <div className='mt-6 sm:mt-8'>
                                <p className='text-sm font-semibold'>Specialisation</p>
                                <ul className='mt-3 space-y-2.5 text-xs text-muted sm:mt-4 sm:space-y-3 sm:text-sm'>
                                    {specialisation.map((item) => (
                                        <li key={item} className='flex gap-2.5 sm:gap-3'>
                                            <span className='mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-[color-mix(in_oklab,var(--color-accent)_70%,transparent)] sm:mt-2' />
                                            <span className='leading-relaxed'>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* CTA */}
                            <div className='mt-8 flex flex-wrap items-center gap-3 sm:mt-10 sm:gap-4'>
                                <a
                                    href='#contact'
                                    className='group inline-flex items-center gap-2.5 rounded-full bg-[color-mix(in_oklab,var(--color-accent)_18%,transparent)] px-4 py-2.5 text-sm font-semibold text-text shadow-[0_14px_40px_rgba(0,0,0,0.10)] transition-transform duration-200 hover:-translate-y-0.5 sm:gap-3 sm:px-5 sm:py-3'>
                                    Work with Nick
                                    <FaArrowRight className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 sm:h-4 sm:w-4' />
                                </a>

                                <p className='text-xs text-muted'>Prefer email first? Use the contact section below.</p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    )
}
